import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { findRouteMeta, formatPathAsTitle, podeAbrirRota } from '@/app/registry/routes';
import { useTabs } from './TabsProvider';

// / e /dashboards só redirecionam para a primeira tela liberada: não viram aba.
const ignoredPaths = ['/', '/dashboards', '/login', '/auth/contaazul/callback', '/api/contaazul/callback', '/contaazul/callback', '/conta-azul/callback'];

export const buildTabKey = (pathname: string, search = ''): string => {
  if (pathname === '/') return '/';
  return `${pathname}${search}`;
};

const shouldIgnorePath = (pathname: string): boolean =>
  ignoredPaths.some((path) => pathname === path || (path !== '/' && pathname.startsWith(`${path}/`)));

const TabRouteSync = () => {
  const location = useLocation();
  const { activeKey, openTab } = useTabs();
  const { hasPermission } = useAuth();
  const currentKey = buildTabKey(location.pathname, location.search);

  const abrirAbaDaRota = useCallback(() => {
    if (shouldIgnorePath(location.pathname)) return;
    // Sem permissão o ProtectedRoute redireciona; abrir a aba antes deixava uma aba da tela proibida (28/10/2026).
    if (!podeAbrirRota(location.pathname, hasPermission)) return;

    const meta = findRouteMeta(location.pathname);
    openTab({
      key: buildTabKey(location.pathname, location.search),
      path: `${location.pathname}${location.search}`,
      title: meta?.title || formatPathAsTitle(location.pathname),
      section: meta?.section || 'Rotina',
      icon: meta?.icon,
      pinned: meta?.tab?.pinned,
      closable: meta?.tab?.closable,
    });
    // hasPermission é recriada a cada render do AuthContext; as permissões só mudam com o usuário.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, openTab]);

  useEffect(() => {
    abrirAbaDaRota();
  }, [abrirAbaDaRota]);

  // A aba da rota atual precisa continuar ativa. Se as abas forem trocadas pelas salvas (login/troca de usuário)
  // depois que a rota abriu a dela, reabre; sem isso a tela ficava em branco com outra aba marcada (28/10/2026).
  useEffect(() => {
    if (activeKey !== null && activeKey !== currentKey) abrirAbaDaRota();
    // Só reage à troca da aba ativa; a troca de rota já é tratada acima.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey]);

  return null;
};

export default TabRouteSync;
